import { Card, Checkbox, message, Modal, Tooltip, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { CompleteTickets, InprogressTickets, TicketDetail } from 'types';
import { requestform } from 'messages';
import { CopyIcon, GreenCheck, PrinterIcon } from 'assets';
import { GetTicketDetail } from 'hooks/request-form';
import {
  usePatchCompleteTicketMutation,
  usePatchInprogressTicketMutation,
  usePatchTransferToCustomerCareCenterMutation
} from 'services';

import defaultTicketDetail from 'types/dto/request-form/request-form.dto';

import { useLocation } from 'react-router-dom';
import {
  useLazyGetWrongCompletedTicketListQuery,
  usePatchConfirmMultipleRequestMutation,
  useRequestUpdateTicketFollowUpMutation
} from 'services/tickets';
import RequestDetailCreateFormInformation from 'components/request-form/RequestDetailCreateFormInformation';
import RequestDetailMainInformation from 'components/request-form/RequestDetailMainInformation';
import ModalObstacle from 'components/request-form/Modal/ModalObstacle';
import ModalTransferCmis from 'components/request-form/Modal/ModalTransferCmis';
import ModalTransferUnit from 'components/request-form/Modal/ModalTransferUnit';
import TicketDetailedIProcessing from './TicketDetailedProcessing';
import TicketStatusBar from './TicketStatusBar';
import PrintComponent from 'components/print-ticket/PrintTikcet';
import { useReactToPrint } from 'react-to-print';
import CloneNormalTicket from 'components/print-ticket-detail/CloneNormalTicket';
import ModalCmisProcessingProgress from 'components/request-form/Modal/ModalCmisProcessingProgress';
import { createTicketMessages } from '../../messages/create-ticket.messages';
import dayjs from 'dayjs';
import RequestDetail from '../request-form/RequestDetail';
import { ConfirmMultipleRequestTickets } from '../../types/dto/ticket';

interface ticketFromDetail {
  status?: string;
  ticketId: number;
  cancelDetailModal: () => void;
  isSecondCall?: boolean;
  currentSessionId?: number | string;
}

const TicketDetailed: React.FC<ticketFromDetail> = ({
  status,
  ticketId,
  cancelDetailModal,
  isSecondCall,
  currentSessionId
}) => {
  const { requestDetail } = GetTicketDetail(ticketId);
  const location = useLocation();

  const [isModal, setIsModal] = useState(false);
  const [isOpenObstacleModal, setIsOpenObstacleModal] = useState(false);
  const [isOpenTransferModal, setIsOpenTransferModal] = useState(false);
  const [processingContent, setProcessingContent] = useState<string>('');
  const [requestUpdateTicketFollowUpContent, setRequestUpdateTicketFollowUpContent] = useState<string>('');
  const [requestItem, setRequestItem] = useState<TicketDetail>(defaultTicketDetail);
  const [isCallAgainClicked, setIsCallAgainClicked] = useState(false);
  const [isHistory, setIsHistory] = useState(false);
  const [outageReasonId, setOutageReasonId] = useState<number>(requestDetail?.additionalInfo?.outageReasonId | 0);
  const [datePowerRestore, setDatePowerRestore] = useState<Date>(
    new Date(requestDetail?.additionalInfo?.powerRestorationAt)
  );
  const [isPowerRestore, setIsPowerRestore] = useState<boolean>(false);
  // Control dispatch API
  const [completeTicket] = usePatchCompleteTicketMutation();
  const [inprocessTicket] = usePatchInprogressTicketMutation();
  const [transferToCustomerCareCenter] = usePatchTransferToCustomerCareCenterMutation();
  const [requestUpdateTicketFollowUp] = useRequestUpdateTicketFollowUpMutation();
  const [isOpenCmisProcess, setIsOpenCmisProcess] = useState(false);
  const [contactEventId, setContactEventId] = useState<number | null>(null);
  const openCmisProcess = () => setIsOpenCmisProcess(true);
  const closeCmisProcess = () => setIsOpenCmisProcess(false);

  const [followUpOptions, setFollowUpOptions] = useState({
    isRequestMultipleTime: false,
    isNotOnTime: false,
    isOutageEntireArea: false,
    isNotRequestToSendSms: false
  });

  useEffect(() => {
    if (requestDetail) {
      const formattedRequestDetail: TicketDetail = Object.assign({}, defaultTicketDetail, requestDetail);
      setRequestItem(formattedRequestDetail);
    }
  }, [requestDetail]);
  useEffect(() => {
    if (!currentSessionId) return;

    try {
      const storedSessions = localStorage.getItem('customerSupportSession');
      if (!storedSessions) return;

      const sessions = JSON.parse(storedSessions);

      // Determine session type based on currentSessionId
      const isCallSession = typeof currentSessionId === 'string';
      // Find the current session
      const currentSession = sessions.find((s: any) =>
        isCallSession ? s.callId === currentSessionId : s.chatSessionId === currentSessionId
      );
      if (currentSession?.contactResponse?.contactEventId) {
        setContactEventId(currentSession.contactResponse.contactEventId);

        // Set default channelId if available
      }
    } catch (error) {
      console.error('❌ Error extracting contactEventId from session:', error);
    }
  }, [currentSessionId, contactEventId]);
  // Control Modal Open / Close
  const openModalCMIS = () => setIsModal(true);
  const closeModalCMIS = () => {
    setIsModal(false);
    cancelDetailModal();
  };
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const openTransferModal = () => setIsOpenTransferModal(true);
  const closeTransferModal = () => setIsOpenTransferModal(false);
  const openObstacleModal = () => setIsOpenObstacleModal(true);
  const closeObstacleModal = () => setIsOpenObstacleModal(false);

  const completeTicketAction = () => {
    if (!processingContent || processingContent.trim() === '') {
      message.error('Vui lòng nhập kết quả xử lý!');
      return;
    }
    Modal.confirm({
      title: 'Xác nhận hoàn thành phiếu yêu cầu',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk() {
        const requestData: CompleteTickets = {
          ticketId,
          processingContent: processingContent,
          outageReasonId: 0,
          powerRestorationAt: ''
        };
        completeTicket(requestData)
          .unwrap()
          .then((res) => {
            message.success('Hoàn thành phiếu yêu cầu', 2);
            cancelDetailModal();
          });
      }
    });
  };

  const inprogressTicketAction = () => {
    if (!processingContent || processingContent.trim() === '') {
      message.error('Vui lòng nhập nội dung xử lý!', 3);
      return;
    }
    const data: InprogressTickets = {
      ticketId,
      processingContent,
      ...followUpOptions
    };
    Modal.confirm({
      title: 'Xác nhận cập nhật phiếu',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk() {
        if (ticketId) {
          inprocessTicket(data)
            .unwrap()
            .then((res) => {
              message.success('Cập nhật phiếu thành công', 2);
              cancelDetailModal();
            })
            .catch((err) => {});
        }
      }
    });
  };
  const requestUpdateTicketFollowUpAction = () => {
    if (!processingContent || processingContent.trim() === '') {
      message.error('Vui lòng nhập nội dung xử lý!', 3);
      return;
    }
    const data: InprogressTickets = {
      ticketId,
      processingContent,
      ...followUpOptions,
      contactEventId: contactEventId || undefined
    };
    // console.log('data nè', data);
    Modal.confirm({
      title: 'Xác nhận cập nhật phiếu',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk() {
        if (ticketId) {
          requestUpdateTicketFollowUp(data)
            .unwrap()
            .then((res) => {
              message.success('Cập nhật phiếu thành công', 2);
              if (selectedIds.length > 0) {
                const ticketIds: ConfirmMultipleRequestTickets = { ticketIds: selectedIds };
                patchTicketMultipleRequestsAction(ticketIds)
                  .unwrap()
                  .then((res) => {
                    // console.log(res);
                  })
                  .catch((err) => {
                    // console.log(err)
                  });
              }
              cancelDetailModal();
              setIsCallAgainClicked(false);
            })
            .catch((err) => {});
        }
      }
    });
  };

  const transferCustomerCareCenter = () => {
    if (!processingContent || processingContent.trim() === '') {
      message.error('Nhập nội dung xử lý trước khi chuyển về TTCSKH', 3);
      return;
    }
    const data: InprogressTickets = {
      ticketId,
      processingContent,
      ...followUpOptions
    };
    Modal.confirm({
      title: 'Xác nhận cập nhật phiếu',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk() {
        if (ticketId) {
          transferToCustomerCareCenter(data)
            .unwrap()
            .then((res) => {
              message.success('Chuyển phiếu về TTCSKH thành công', 2);
              cancelDetailModal();
            })
            .catch((err) => {});
        }
      }
    });
  };

  const [trigger, { data: wrongTicketData, isFetching }] = useLazyGetWrongCompletedTicketListQuery();
  const [patchTicketMultipleRequestsAction] = usePatchConfirmMultipleRequestMutation();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const handleCheckboxChange = (ticketId: number, checked: boolean) => {
    if (checked) setSelectedIds((prev) => [...prev, ticketId]);
    else setSelectedIds((prev) => prev.filter((id) => id !== ticketId));
  };

  const [autoCheckedIds, setAutoCheckedIds] = useState<number[]>([]);
  useEffect(() => {
    if (wrongTicketData?.data?.rows) {
      const autoIds = wrongTicketData.data.rows
        .filter((item) => item.isRequestMultipleTime)
        .map((item) => item.ticketId);

      setAutoCheckedIds(autoIds);
    }
  }, [wrongTicketData]);
  const [detailTicket, setDetailTicket] = useState<number>(0);
  const [modalDetailTickets, setModalDetailTickets] = useState<boolean>(false);
  const openDetailTicket = (id: number) => {
    setDetailTicket(id);
    setModalDetailTickets(true);
  };
  const closeDetailTicket = () => setModalDetailTickets(false);

  useEffect(() => {
    if (!requestItem) return;

    const { customerRequestPhoneNumber: phone, customerCode, serviceType } = requestItem;
    const filled = Boolean(phone && customerCode && serviceType?.serviceTypeId);

    setIsInfoFilled(filled);

    trigger({
      pageIndex: 1,
      pageSize: 100,
      customerCode: customerCode || 'khongdinhdanh',
      customerRequestPhoneNumber: phone,
      serviceTypeId: serviceType?.serviceTypeId || 1
    });
  }, [requestItem]);

  const [isInfoFilled, setIsInfoFilled] = useState(false);

  return (
    <Card
      title={
        <Typography.Text className='w-full text-center text-[16px]/[22px] font-semibold'>
          {requestform.requestTicket} {ticketId} 
        </Typography.Text>
      }
      className='request-form-detail-card text-center'
    >
      <div style={{ display: 'none' }}>
        <PrintComponent ref={contentRef}>
          <CloneNormalTicket ticket={requestDetail} status={status} ticketId={ticketId} />
        </PrintComponent>
      </div>
      <div className='flex w-full text-left'>
        <div className='w-[250px]'>
          <RequestDetailCreateFormInformation requestItem={requestItem} />
        </div>
        <section className='flex flex-col justify-between'>
          <section className='h-[calc(95vh-8rem) w-[870px] overflow-y-auto p-6'>
            <section className='flex items-center justify-between'>
              <div className='w-4/5'>
                <Typography.Text className='text-[26px]/[34px] font-bold text-[#141414]'>
                  {requestItem?.serviceType && requestItem?.serviceType?.name ? requestItem?.serviceType?.name : ''}
                </Typography.Text>
              </div>
              <button onClick={() => reactToPrintFn()} className='flex items-center space-x-2'>
                <PrinterIcon />
                <Typography.Text className='text-[16px]/[20px] text-[#1564E8]'>
                  {requestform.printTicket}
                </Typography.Text>
              </button>
            </section>

            <section>
              <Typography.Text
                className={`text-[14px]/[18px] font-normal italic ${
                  requestItem?.numOfRequests > 5
                    ? 'text-[#D1131D]'
                    : requestItem?.numOfRequests > 3
                      ? 'text-[#FF9500]'
                      : 'text-[#6C737E]'
                }`}
              >
                {requestform.customerContact} {requestItem?.numOfRequests || 0} {requestform.customerContractTime}
              </Typography.Text>
            </section>

            <section>
              {status && (
                <section className='pb-6'>
                  <TicketStatusBar status={status} />
                  {/* CMIS Transfer */}
                  {requestItem?.serviceTypeId === 1 ||
                  requestItem?.serviceTypeId === 3 ||
                  requestItem?.serviceTypeId === 4 ? (
                    ''
                  ) : requestItem.cmisRequestId ? (
                    <button
                      onClick={openCmisProcess}
                      className='mt-1 flex w-full justify-between rounded-[5px] bg-[#06A77D00] py-[11px] hover:bg-[#F5F5F7]'
                    >
                      <div className='flex space-x-1'>
                        <GreenCheck />
                        <Typography.Text className='text-[14px]/[18px] italic text-[#141414]'>
                          {requestform.cmisTranfered + requestItem?.cmisRequestId}
                        </Typography.Text>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const textToCopy = requestItem?.cmisRequestId || '';

                            // Kiểm tra hỗ trợ Clipboard API và context bảo mật
                            if (navigator.clipboard && window.isSecureContext) {
                              navigator.clipboard
                                .writeText(textToCopy)
                                .then(() => {
                                  message.success('Mã đã được sao chép!');
                                })
                                .catch(() => {
                                  message.error('Sao chép thất bại, vui lòng thực hiện lại sau');
                                });
                            } else {
                              // Phương án dự phòng cho trình duyệt cũ
                              try {
                                const textArea = document.createElement('textarea');
                                textArea.value = textToCopy;
                                // Ẩn textarea khỏi màn hình
                                textArea.style.position = 'fixed';
                                textArea.style.left = '-999999px';
                                textArea.style.top = '-999999px';
                                document.body.appendChild(textArea);
                                textArea.focus();
                                textArea.select();
                                document.execCommand('copy');
                                textArea.remove();
                                message.success('Mã đã được sao chép!');
                              } catch (err) {
                                message.error('Sao chép thất bại, vui lòng thực hiện lại sau');
                              }
                            }
                          }}
                        >
                          <Tooltip placement='right' title='Sao chép mã CMIS'>
                            <CopyIcon className={'hover:h-4 hover:w-4'} />
                          </Tooltip>
                        </button>
                      </div>{' '}
                    </button>
                  ) : (
                    <div className='mt-1 flex justify-between rounded-[5px] border border-[#D1131D] bg-[#06A77D00] px-4 py-[11px]'>
                      <div className='flex items-center space-x-2'>
                        <Typography.Text className='text-[14px]/[18px] font-semibold text-[#D1131D]'>
                          {requestform.cmisTransferFail}
                        </Typography.Text>{' '}
                        {/* <Typography.Text className='text-[14px]/[18px] text-[#141414]'>
                              <Dropdown overlay={menu} trigger={['click']}>
                                <div className='flex w-full cursor-pointer items-center justify-between space-x-2 border-[#EBEBED] bg-[#FFFFFF]'>
                                  <Typography.Text className='text-[14px]/[18px]'>
                                    {selectedOption?.name ? selectedOption?.name : requestform.typeCmisTicket}
                                  </Typography.Text>
                                  <DownOutlined className='h-2 w-[10px]' />
                                </div>
                              </Dropdown>
                            </Typography.Text>{' '} */}
                      </div>
                      {/* <button onClick={transferCmis}>
                            <Typography.Text className='text-[14px]/[18px] text-[#1564E8]'>
                              {requestform.tryAgainButton}
                            </Typography.Text>
                          </button> */}
                    </div>
                  )}
                </section>
              )}
            </section>

            <section
              className={`${
                status === 'completed' ||
                (requestItem?.serviceTypeId !== 1 &&
                  requestItem?.serviceTypeId !== 3 &&
                  requestItem?.serviceTypeId !== 4 &&
                  isHistory)
                  ? 'h-[calc(70vh)] overflow-y-auto'
                  : 'max-h-[calc(90vh-20rem)] overflow-y-auto'
              }`}
            >
              <RequestDetailMainInformation requestItem={requestItem} isHistory={true} />
              {requestDetail?.status?.code !== 'HOANTAT' && (
                <section className='mt-4 flex flex-row gap-6'>
                  {isInfoFilled && (
                    <div className={'w-full'}>
                      <div className={'mb-2 w-full'}>
                        <Typography.Text className='mb-6 text-left text-[16px]/[20px] font-semibold text-[#141414]'>
                          {createTicketMessages.wrongCompletedTicketList}
                        </Typography.Text>
                      </div>

                      <div className={''}>
                        {wrongTicketData?.data?.rows?.length
                          ? wrongTicketData.data.rows.map((item) => (
                              <div
                                onClick={() => openDetailTicket(item.ticketId)}
                                className={
                                  'mt-2 flex w-full cursor-pointer items-center justify-between space-x-4 rounded-lg bg-black/10 p-4 hover:bg-black/15'
                                }
                              >
                                <Checkbox
                                  key={item?.ticketId}
                                  checked={item?.isRequestMultipleTime || selectedIds.includes(item.ticketId)}
                                  disabled={!isCallAgainClicked}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    if (!isCallAgainClicked) return;
                                    e.stopPropagation();
                                    handleCheckboxChange(item.ticketId, e.target.checked);
                                  }}
                                  className={'w-1/5'}
                                >
                                  <div>
                                    <Typography.Text>{item?.ticketId}</Typography.Text>
                                  </div>
                                </Checkbox>

                                <div className={'w-2/5'}>
                                  <Typography.Text className={'font-semibold'}>Ngày tạo:</Typography.Text>{' '}
                                  {item?.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY HH:mm:ss') : ''}
                                </div>
                                <div className={'w-2/5'}>
                                  <Typography.Text className={'font-semibold'}>Ngày đóng:</Typography.Text>{' '}
                                  {item?.closedAt ? dayjs(item.closedAt).format('DD/MM/YYYY HH:mm:ss') : ''}
                                </div>
                              </div>
                            ))
                          : !isFetching && <Typography.Text>Không có phiếu đóng sai quy định</Typography.Text>}

                        <Modal
                          centered
                          className='ant-modal-body-p-none h-[95vh] w-fit rounded-xl bg-[#F5F4F6]'
                          open={modalDetailTickets}
                          onCancel={closeDetailTicket}
                          footer={null}
                          destroyOnClose
                        >
                          <RequestDetail ticketId={detailTicket} cancelDetailModal={closeDetailTicket} />
                        </Modal>
                      </div>
                    </div>
                  )}
                </section>
              )}
              <TicketDetailedIProcessing
                requestItem={requestItem}
                processingContent={processingContent}
                setProcessingContent={setProcessingContent}
                setRequestUpdateTicketFollowUpContent={setRequestUpdateTicketFollowUpContent}
                isSecondCall={isSecondCall || isCallAgainClicked}
                setOutageReasonId={setOutageReasonId}
                setIsPowerRestore={setIsPowerRestore}
                setDatePowerRestore={setDatePowerRestore}
                isHistory={isHistory}
                followUpOptions={followUpOptions}
                setFollowUpOptions={setFollowUpOptions}
              />
            </section>
          </section>

          {/* Button */}
          {!location.pathname.includes('customer-lookup') && status !== 'completed' && (
            <div className='flex h-[4rem] items-center justify-end border-t-2 p-6 text-left'>
              {status === 'pending_reception' && (
                <button
                  onClick={openModalCMIS}
                  className='rounded-lg bg-[#1564E8] px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#FFFFFF]'
                >
                  {requestform.acceptTicket}
                </button>
              )}

              <div className='flex space-x-4'>
                {(status === 'in_progress' || status === 'collaboration') && (
                  <>
                    <button
                      onClick={openObstacleModal}
                      className='rounded-lg border border-[#D1131D] px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#D1131D]'
                    >
                      {requestform.obstacleTickets}
                    </button>

                    {status !== 'collaboration' && (
                      <>
                        <button
                          onClick={transferCustomerCareCenter}
                          className='rounded-lg border border-[#D1131D] bg-transparent px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#D1131D]'
                        >
                          {requestform.transferCustomerCareCenter}
                        </button>
                        <button
                          onClick={openTransferModal}
                          className='rounded-lg border border-[#1564E8] bg-transparent px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#1564E8]'
                        >
                          {requestform.transferCollaboration}
                        </button>
                      </>
                    )}

                    <button
                      onClick={inprogressTicketAction}
                      className='rounded-lg bg-[#FF9500] px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#FFFFFF]'
                    >
                      {requestform.inprocessTicket}
                    </button>
                    <button
                      onClick={completeTicketAction}
                      className='rounded-lg bg-[#28A745] px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#FFFFFF]'
                    >
                      {requestform.completedButton}
                    </button>
                  </>
                )}

                {status === 'obstacle' && (
                  <>
                    <button
                      // onClick={inprogressTicketAction}
                      className='rounded-lg bg-[#FF9500] px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#FFFFFF]'
                    >
                      {requestform.updateObstacleTicket}
                    </button>
                    <button
                      // onClick={completeTicketAction}
                      className='rounded-lg bg-[#28A745] px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#FFFFFF]'
                    >
                      {requestform.completedObstacleTicket}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {location.pathname.includes('customer-lookup') && status !== 'HOANTAT' && status !== 'HUY' && (
            <div className='flex h-[4rem] items-center justify-end border-t-2 p-6 text-left'>
              <button
                onClick={() => {
                  if (isCallAgainClicked) {
                    requestUpdateTicketFollowUpAction();
                  } else {
                    setIsCallAgainClicked(true);
                  }
                }}
                className='rounded-lg bg-[#1564E8] px-6 py-[10px] text-[16px]/[20px] font-semibold text-[#FFFFFF]'
              >
                {isSecondCall || isCallAgainClicked ? 'Cập nhật' : requestform.callAgain}
              </button>
            </div>
          )}
        </section>
      </div>
      <Modal
        centered
        className='ant-modal-body-p-none w-[80vw] rounded-xl bg-[#FFFFFF]'
        open={isOpenCmisProcess}
        onCancel={closeCmisProcess}
        footer={null}
        destroyOnClose
      >
        <ModalCmisProcessingProgress cmisRequestId={requestItem.cmisRequestId} closeCmisProcess={closeCmisProcess} />
      </Modal>
      <Modal
        centered
        className='ant-modal-body-p-none min-w-[568px] rounded-xl bg-[#F5F4F6]'
        open={isOpenObstacleModal}
        onCancel={closeObstacleModal}
        footer={null}
        destroyOnClose
      >
        <ModalObstacle ticketId={ticketId} closeObstacleModal={closeObstacleModal} />
      </Modal>

      <Modal
        centered
        className='ant-modal-body-p-none min-w-[568px] rounded-xl bg-[#F5F4F6]'
        open={isModal}
        onCancel={closeModalCMIS}
        footer={null}
        destroyOnClose
      >
        <ModalTransferCmis requestItem={requestItem} closeModalCMIS={closeModalCMIS} />
      </Modal>

      <Modal
        centered
        className='ant-modal-body-p-none min-w-[568px] rounded-xl bg-[#F5F4F6]'
        open={isOpenTransferModal}
        onCancel={closeTransferModal}
        footer={null}
        destroyOnClose
      >
        <ModalTransferUnit
          requestItem={requestItem}
          processingContent={processingContent}
          cancelDetailModal={cancelDetailModal}
        />
      </Modal>
    </Card>
  );
};

export default TicketDetailed;
